import axios from "axios";
import { message } from "antd";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import Compress from "./Compress";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const limpiarNombreArchivo = (texto) => (texto || "").replace(/[/\\?*[\]:#]/g, "_");

// Recibos por petición. Debe ir igual o por debajo de MAX_RECIBOS_PARTES_PDF del
// backend (Controllers/Prenomina/cierre.controllers.js). El servidor ya no renderiza
// —sólo arma el contenido, ~15 consultas para toda la tanda—, así que las tandas ya
// no tienen que ser chicas.
const TAMANO_TANDA_PDF = 100;

// pdfmake muta lo que recibe (los `widths` de las tablas, el watermark), y aquí el
// mismo `docBase` arma un documento por recibo: cada uno lleva su propia copia.
const copiar = (objeto) => JSON.parse(JSON.stringify(objeto));

const renderizarBlob = (definicion) =>
  new Promise((resolve) => pdfMake.createPdf(definicion).getBlob(resolve));

const enTandas = (arreglo, tamano) => {
  const tandas = [];
  for (let i = 0; i < arreglo.length; i += tamano) tandas.push(arreglo.slice(i, i + tamano));
  return tandas;
};

/**
 * Descarga masiva de recibos de nómina (XML timbrado y/o PDF) en un solo ZIP.
 * Reutilizable desde PreNomina (una nómina) y desde el módulo de consulta
 * masiva de Recibos (líneas de distintos periodos/empleados).
 *
 * El CONTENIDO del PDF lo arma el backend (Pdf/reciboNominaPdf.js), que es el único
 * generador del recibo: así lo que se baja aquí es el mismo documento que se ve en
 * la vista previa y que se adjunta al correo, marca de agua de borrador incluida.
 * El RENDER corre aquí, en el navegador, igual que en el visor (ModalVerRecibos).
 * Antes lo hacía el servidor (prenomina/recibosNominaMasivo), en su hilo principal:
 * cada tanda lo dejaba sin atender a nadie varios segundos, y una descarga grande
 * saturó el servidor para todas las empresas el 20/10/2026.
 *
 * @param {Object} params
 * @param {string} params.empresaId
 * @param {Object} params.headersToken
 * @param {string[]} params.lineaIds - ids de LineaPrenomina (para XML, solo las timbradas tienen)
 * @param {'xml'|'pdf'|'ambos'} params.formato
 * @param {string} [params.nombreZip]
 */
const descargarRecibosMasivo = async ({ empresaId, headersToken, lineaIds, formato, nombreZip }) => {
  if (!lineaIds || lineaIds.length === 0) {
    message.info("No hay recibos para descargar");
    return;
  }

  const archivos = [];

  // El nombre ya no lleva el UUID —que era único— sino empleado+periodo, así que dos homónimos
  // del mismo periodo caerían en el mismo archivo y JSZip pisa en silencio (ver Compress.js).
  //
  // La llave del desempate es lineaId|division y NO el nombre suelto: el recorrido del XML va en
  // el orden de CfdiNomina y el del PDF en el de lineaIds, así que con contadores independientes
  // el "_2" del PDF podría caerle a un empleado y el del XML al otro, y quedarían cruzados. Así,
  // el segundo recorrido reutiliza la base que fijó el primero y la pareja no se separa.
  const basePorRecibo = new Map(); // `${lineaId}|${division}` -> base ya desempatada
  const conteoPorBase = new Map(); // base -> cuántas veces se ha usado

  const baseUnica = (nombreBase, lineaId, division) => {
    const llave = `${lineaId}|${division ?? ""}`;
    if (basePorRecibo.has(llave)) return basePorRecibo.get(llave);
    const n = (conteoPorBase.get(nombreBase) || 0) + 1;
    conteoPorBase.set(nombreBase, n);
    const base = n === 1 ? nombreBase : `${nombreBase}_${n}`;
    basePorRecibo.set(llave, base);
    return base;
  };

  try {
    if (formato === "xml" || formato === "ambos") {
      const res = await axios.post(
        "prenomina/xmlCfdiMasivo",
        { empresaId, lineaIds },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        for (const r of res.data.recibos) {
          if (!r.xml) continue;
          // El backend manda el mismo nombre que le toca al PDF de este recibo; el UUID queda
          // de respaldo por si un recibo viejo no trajera nombre.
          const base = limpiarNombreArchivo(r.nombreBase) || `${r.uuid || r.lineaId}`;
          archivos.push({
            nombre: `${baseUnica(base, r.lineaId, r.division)}.xml`,
            contenido: r.xml,
          });
        }
      }
    }

    if (formato === "pdf" || formato === "ambos") {
      for (const tanda of enTandas(lineaIds, TAMANO_TANDA_PDF)) {
        const res = await axios.post(
          "prenomina/recibosNominaPartesPdf",
          // Un documento por recibo, no el contenido concatenado del visor.
          { empresaId, lineaIds: tanda, separados: true },
          { headers: headersToken }
        );
        if (res.data.success !== 2) continue;
        const { logo, docBase, watermarkSinTimbrar } = res.data;
        // En serie y con await: entre un render y otro el navegador alcanza a pintar.
        for (const recibo of res.data.recibos) {
          // Misma forma que reciboNominaPdf() del backend: el watermark se decide por el
          // CFDI de ESTE recibo, no por el lote.
          const blob = await renderizarBlob({
            ...(recibo.esSinTimbrar && watermarkSinTimbrar ? { watermark: copiar(watermarkSinTimbrar) } : {}),
            ...copiar(docBase),
            content: recibo.contenido,
            images: logo ? { logo } : {},
          });
          const base = limpiarNombreArchivo(recibo.nombreBase) || `Recibo_${recibo.lineaId}`;
          archivos.push({
            nombre: `${baseUnica(base, recibo.lineaId, recibo.division)}.pdf`,
            contenido: blob,
          });
        }
      }
    }

    if (archivos.length === 0) {
      message.warning("No se encontraron recibos para descargar");
      return;
    }

    Compress(archivos, nombreZip || "recibos_nomina");
  } catch (err) {
    console.error("Error al descargar recibos masivo:", err);
    message.error("Error al descargar los recibos");
  }
};

export default descargarRecibosMasivo;
